import React, { useState, useEffect, useContext } from 'react'
import { Container } from './Container'
import { useNavigate } from 'react-router-dom'
import { ChatSupportContext } from '../../contexts/ChatSupportContext'

export function CallToAction() {
  const [isVisible, setIsVisible] = useState(false);
  const [pulseAnimation, setPulseAnimation] = useState(false);
  
  // Navigation hook for routing
  const navigate = useNavigate();
  
  // Get the chat support ref from context
  const { chatSupportRef } = useContext(ChatSupportContext);

  // Handle the contact us button click
  const handleContactClick = (e) => {
    e.preventDefault();
    // Open the chat without specifying a category
    if (chatSupportRef && chatSupportRef.current) {
      chatSupportRef.current.openChat();
    }
  };
  
  useEffect(() => {
    setIsVisible(true);
    
    // Start pulse animation after a delay
    const pulseTimer = setTimeout(() => {
      setPulseAnimation(true);
    }, 2000);
    
    // Pulse animation interval
    const animationInterval = setInterval(() => {
      setPulseAnimation(prev => !prev);
    }, 4000);
    
    return () => {
      clearTimeout(pulseTimer);
      clearInterval(animationInterval);
    };
  }, []);

  // Simple navigation function
  function goToRegister() {
    navigate('/register');
  }

  return (
    <section
      id="get-started"
      className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 py-32"
    >
      {/* Professional geometric background */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Digital grid */}
        <div className="absolute inset-0 opacity-10">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M0 40L40 0" stroke="white" strokeWidth="0.5" fill="none" />
                <path d="M0 80L80 0" stroke="white" strokeWidth="0.5" fill="none" />
                <path d="M0 120L120 0" stroke="white" strokeWidth="0.5" fill="none" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>
        
        {/* Professional accent elements */}
        <div className="absolute top-0 right-0 w-1/3 h-80 bg-white opacity-5 rounded-bl-full transform -translate-y-1/4 translate-x-1/4"></div>
        <div className="absolute bottom-0 left-0 w-1/3 h-80 bg-white opacity-5 rounded-tr-full transform translate-y-1/4 -translate-x-1/4"></div>
        
        {/* Modern tech accent */}
        <div className="absolute left-1/4 top-1/3 w-1/2 h-1/3">
          <svg viewBox="0 0 300 100" xmlns="http://www.w3.org/2000/svg" className="opacity-5">
            <line x1="0" y1="50" x2="300" y2="50" stroke="white" strokeWidth="0.5" strokeDasharray="4,3" />
            <line x1="150" y1="0" x2="150" y2="100" stroke="white" strokeWidth="0.5" strokeDasharray="4,3" />
            <circle cx="150" cy="50" r="40" stroke="white" fill="none" strokeWidth="0.5" />
            <circle cx="150" cy="50" r="20" stroke="white" fill="none" strokeWidth="0.5" />
            <circle cx="150" cy="50" r="5" fill="white" />
          </svg>
        </div>
      </div>
      
      <Container className="relative">
        <div 
          className={`mx-auto max-w-2xl text-center transition-all duration-1000 transform ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}
        >
          <div className="inline-flex items-center justify-center mb-3">
            <div className="h-0.5 w-10 bg-blue-300 opacity-70"></div>
            <div className="mx-3 text-blue-200 font-medium text-sm uppercase tracking-wider">The Future of Audit</div>
            <div className="h-0.5 w-10 bg-blue-300 opacity-70"></div>
          </div>
          
          <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
            Join the <span className="relative inline-block">
              <span className="relative z-10">Audit Revolution</span>
              <span className="absolute bottom-0 left-0 w-full h-2 bg-blue-400/30"></span>
            </span>
          </h2>
          <p className="mt-6 text-lg leading-8 text-blue-100">
            Reimagine what's possible when AI meets audit expertise. Step into the future of compliance with seamless automation, intelligent insights, and unprecedented efficiency. Your audit transformation begins here.
          </p>
        </div>
          
        {/* Professional CTA buttons with modern design - moved outside nested containers */}
        <div 
          className={`mx-auto mt-10 flex flex-col sm:flex-row justify-center gap-4 transition-all duration-1000 delay-300 transform ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}
          style={{ zIndex: 100, position: 'relative' }}
        >
          {/* Schedule Demo button with increased specificity and isolation */}
          <button 
            onClick={goToRegister}
            className="bg-white text-slate-900 rounded-lg px-8 py-4 font-medium text-lg shadow-lg hover:shadow-xl active:bg-gray-100 relative isolate"
            style={{ zIndex: 999, isolation: 'isolate' }}
          >
            <div className="flex items-center justify-center pointer-events-auto">
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className="h-5 w-5 mr-3 text-blue-600" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth="2" 
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" 
                />
              </svg>
              Schedule a Demo
            </div>
          </button>
          
          {/* Contact Us button */}
          <button 
            onClick={handleContactClick}
            className="bg-blue-600/20 backdrop-blur-sm text-white border border-white/30 rounded-lg px-8 py-4 font-medium text-lg shadow-lg hover:shadow-xl hover:bg-blue-600/30 active:bg-blue-600/40 relative isolate"
            style={{ zIndex: 999, isolation: 'isolate' }}
          >
            <div className="flex items-center justify-center pointer-events-auto">
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className="h-5 w-5 mr-3 text-blue-100" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth="2" 
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" 
                />
              </svg>
              Contact Us
            </div>
          </button>
        </div>
        
        {/* Professional decorative elements */}
        <div className="absolute left-0 bottom-0 w-32 h-32 opacity-20" style={{ zIndex: 1 }}>
          <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
            <circle cx="50" cy="50" r="40" stroke="white" strokeWidth="0.5" fill="none" />
            <circle cx="50" cy="50" r="30" stroke="white" strokeWidth="0.5" fill="none" />
            <circle cx="50" cy="50" r="20" stroke="white" strokeWidth="0.5" fill="none" />
          </svg>
        </div>
        
        <div className="absolute right-0 top-0 w-32 h-32 opacity-20" style={{ zIndex: 1 }}>
          <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
            <rect x="10" y="10" width="80" height="80" stroke="white" strokeWidth="0.5" fill="none" />
            <rect x="25" y="25" width="50" height="50" stroke="white" strokeWidth="0.5" fill="none" />
            <rect x="40" y="40" width="20" height="20" stroke="white" strokeWidth="0.5" fill="none" />
          </svg>
        </div>
        
        {/* Data flow visualization */}
        <div className="absolute right-1/4 bottom-0 opacity-10" style={{ zIndex: 1 }}>
          <svg width="200" height="100" viewBox="0 0 200 100" xmlns="http://www.w3.org/2000/svg">
            <path d="M0,50 C50,20 150,80 200,50" stroke="white" strokeWidth="1" fill="none" />
            <circle cx="0" cy="50" r="2" fill="white" />
            <circle cx="50" cy="20" r="2" fill="white" />
            <circle cx="150" cy="80" r="2" fill="white" />
            <circle cx="200" cy="50" r="2" fill="white" />
            <circle cx="100" cy="50" r="3" fill="white" />
          </svg>
        </div>
      </Container>
    </section>
  )
} 